import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import localeDe from '@angular/common/locales/de';
import localeDeExtra from '@angular/common/locales/extra/de';
import { registerLocaleData } from '@angular/common';
import { MatIconRegistry } from '@angular/material/icon';
import { KeyCloakService } from './core/services/keycloak.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import {
  MatSidenav,
  MatSidenavContainer,
  MatSidenavContent,
} from '@angular/material/sidenav';
import { ConfigService } from './core/services/config.service';
import { IPhysician } from './interfaces/IPhysician.interface';
import { MessagesService } from './services/messages.service';
import { UsersService } from './services/users.service';
import { HeaderComponent } from './components/shared/header/header.component';
import { MenuTreeComponent } from './components/shared/menu-tree/menu-tree.component';
import { IYesNoDialogData } from './interfaces/IYesNoDialogData.interface';
import { DialogService } from './services/dialog.service';
import { UserDetailsCardComponent } from './components/shared/user-details-card/user-details-card.component';
import { AppLayoutService } from './core/services/app-layout.service';
import { ResponsiveService } from './services/responsive.service';
import { ApiHealthService } from './services/api.health.service';
import { VersionDisplay } from './components/shared/version-display/version-display.component';
import { FeatureFlagService } from './services/feature-flag.service';
import { TrackingService } from './core/services/tracking.service';
import { StaticDataService } from './services/static-data.service';
import { UserAgentOutdatedBanner } from './components/shared/user-agent-outdated-banner/user-agent-outdated-banner';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MatSidenavContainer,
    MatSidenavContent,
    MatSidenav,
    MenuTreeComponent,
    HeaderComponent,
    UserDetailsCardComponent,
    TranslatePipe,
    VersionDisplay,
    UserAgentOutdatedBanner,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private readonly _keyCloakService: KeyCloakService = inject(KeyCloakService);
  private readonly _translateService: TranslateService =
    inject(TranslateService);
  private readonly _titleService: Title = inject(Title);
  private readonly _responsiveService = inject(ResponsiveService);
  private readonly _messagesService: MessagesService = inject(MessagesService);
  private readonly _dialogService: DialogService = inject(DialogService);
  private readonly _usersService: UsersService = inject(UsersService);
  private readonly _configService: ConfigService = inject(ConfigService);
  private readonly _layoutService = inject(AppLayoutService);
  private readonly _apiHealthService: ApiHealthService =
    inject(ApiHealthService);
  private readonly _staticDataService: StaticDataService =
    inject(StaticDataService);
  private readonly _featureFlagService = inject(FeatureFlagService);
  private readonly _trackingService = inject(TrackingService);

  private readonly _matIconReg: MatIconRegistry = inject(MatIconRegistry);
  private readonly _router: Router = inject(Router);
  private readonly _sanitizer = inject(DomSanitizer);

  protected readonly _kammerName = this._configService.kammerName;

  protected physician: IPhysician | null = null;
  protected isXSmallViewport = computed(() =>
    this._responsiveService.isMobile(),
  );

  protected sidenavOpen = model<boolean>(false);
  public isLoaded = signal(false);
  public apiIsRunning = signal(false);
  protected showLayout = computed(() => this._layoutService.showLayout());
  protected showUserCard = computed(() =>
    this._featureFlagService.isUserCardInMenuTree(),
  );

  @ViewChild('sidenav')
  public sidenav!: MatSidenav;

  constructor() {
    this.setupLocaleAndTitle();
    this._apiHealthService.connection.subscribe(async (isRunning) => {
      this.apiIsRunning.set(isRunning);
    });

    effect(async () => {
      const start = this.apiIsRunning();
      if (start) {
        await this.initApp();
      }
    });
  }

  public toggleSidebar() {
    this.sidenav.toggle();
    this.sidenavOpen.set(!this.sidenavOpen());
  }

  public onRouterOutletActivate() {
    if (!this.sidenav) {
      return;
    }

    if (this.isXSmallViewport() && this.sidenav.opened) {
      this.toggleSidebar();
    }
    this.sidenavOpen.set(false);
  }

  private async AGBs(): Promise<void> {
    const agreements = await this._usersService.getUserAgreements();
    if (!agreements) {
      this.apiIsRunning.set(true);
      this.isLoaded.set(false);
      return;
    }
    const AGBs = agreements!.find(
      (a) =>
        a.agreement_type?.agreement_id ===
        this._configService.agreementsConfig.agbsId,
    );
    if (!AGBs) {
      this.apiIsRunning.set(true);
      this.isLoaded.set(false);
      return;
    }
    if (AGBs!.agreed) {
      return;
    }
    const content = await this._staticDataService.getAGBs();
    const data: IYesNoDialogData = {
      content: content,
      yesButton: this._translateService.instant('buttons.accept'),
      noButton: this._translateService.instant('buttons.dont-accept'),
      appScrollNearEnd: true,
    };
    this._dialogService.openYesNoDialog(data).subscribe(async (r) => {
      if (r) {
        await this._usersService.putUserAgreement(
          AGBs!.agreement_type.agreement_id,
          r,
        );
      } else {
        this.logout();
      }
    });
  }

  async initApp() {
    // 1. Handle Authentication
    if (!this._keyCloakService.loggedIn()) {
      this._keyCloakService.login();
      return;
    }

    // 2. Load Data only if Auth + API are good
    try {
      const kto_id = await this._keyCloakService.getKtoId();

      // Add tracking Id
      this._trackingService.addUserTrackingIdentifier(kto_id);

      if (this._featureFlagService.isChatEnabled()) {
        await this._messagesService.initialize();
      }

      if (this._featureFlagService.isAgreementsEnabled()) {
        await this.AGBs();
      }

      this.isLoaded.set(true);
    } catch {
      this._router.navigateByUrl('error');
    }
  }

  private setupLocaleAndTitle() {
    registerLocaleData(localeDe, 'de-DE', localeDeExtra);
    this._matIconReg.setDefaultFontSetClass('material-symbols-outlined');
    this._matIconReg.addSvgIcon(
      'error-illustration',
      this._sanitizer.bypassSecurityTrustResourceUrl('assets/error.svg'),
    );
    this._translateService.use('de');

    this._translateService.onLangChange.subscribe(() => {
      this._translateService
        .get('index.title', { kammerName: this._kammerName })
        .subscribe((title: string) => {
          this._titleService.setTitle(title);
        });
    });
  }

  public logout() {
    this._keyCloakService.logout({
      redirectUri: window.location.origin,
    });
  }
}
